/**
 * SVG Icons for SafePassage
 * Clean, flat icons suitable for road safety simulation
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { theme } from '@core/theme';

export type IconName =
  | 'pedestrian'
  | 'cyclist'
  | 'driver'
  | 'car'
  | 'traffic-light'
  | 'crosswalk'
  | 'warning'
  | 'check'
  | 'eye'
  | 'clock'
  | 'arrow-right';

/**
 * Create an icon as a PixiJS Graphics object
 */
export function createIcon(name: IconName, size = 24, color: number = theme.colors.textPrimary): Container {
  const container = new Container();
  const g = new Graphics();
  container.addChild(g);

  const scale = size / 24; // Base size is 24

  switch (name) {
    case 'pedestrian':
      drawPedestrian(g, scale, color);
      break;
    case 'cyclist':
      drawCyclist(g, scale, color);
      break;
    case 'driver':
      drawDriver(g, scale, color);
      break;
    case 'car':
      drawCar(g, scale, color);
      break;
    case 'traffic-light':
      drawTrafficLight(g, scale, color);
      break;
    case 'crosswalk':
      drawCrosswalk(g, scale, color);
      break;
    case 'warning':
      drawWarning(g, scale, color);
      break;
    case 'check':
      drawCheck(g, scale, color);
      break;
    case 'eye':
      drawEye(g, scale, color);
      break;
    case 'clock':
      drawClock(g, scale, color);
      break;
    case 'arrow-right':
      drawArrowRight(g, scale, color);
      break;
  }

  return container;
}

function drawPedestrian(g: Graphics, scale: number, color: number): void {
  // Head
  g.circle(12 * scale, 4 * scale, 3 * scale);
  g.fill(color);

  // Body
  g.moveTo(12 * scale, 7 * scale);
  g.lineTo(12 * scale, 14 * scale);
  g.stroke({ width: 2 * scale, color });

  // Arms
  g.moveTo(8 * scale, 10 * scale);
  g.lineTo(16 * scale, 10 * scale);
  g.stroke({ width: 2 * scale, color });

  // Legs
  g.moveTo(12 * scale, 14 * scale);
  g.lineTo(8 * scale, 22 * scale);
  g.stroke({ width: 2 * scale, color });

  g.moveTo(12 * scale, 14 * scale);
  g.lineTo(16 * scale, 22 * scale);
  g.stroke({ width: 2 * scale, color });
}

function drawCyclist(g: Graphics, scale: number, color: number): void {
  // Wheels
  g.circle(5 * scale, 18 * scale, 4 * scale);
  g.stroke({ width: 1.5 * scale, color });

  g.circle(19 * scale, 18 * scale, 4 * scale);
  g.stroke({ width: 1.5 * scale, color });

  // Frame
  g.moveTo(5 * scale, 18 * scale);
  g.lineTo(12 * scale, 12 * scale);
  g.lineTo(19 * scale, 18 * scale);
  g.stroke({ width: 1.5 * scale, color });

  g.moveTo(12 * scale, 12 * scale);
  g.lineTo(12 * scale, 8 * scale);
  g.stroke({ width: 1.5 * scale, color });

  // Head
  g.circle(12 * scale, 5 * scale, 2.5 * scale);
  g.fill(color);
}

function drawDriver(g: Graphics, scale: number, color: number): void {
  // Steering wheel
  g.circle(12 * scale, 14 * scale, 6 * scale);
  g.stroke({ width: 2 * scale, color });

  // Spokes
  g.moveTo(12 * scale, 8 * scale);
  g.lineTo(12 * scale, 12 * scale);
  g.stroke({ width: 1.5 * scale, color });

  g.moveTo(6 * scale, 14 * scale);
  g.lineTo(10 * scale, 14 * scale);
  g.stroke({ width: 1.5 * scale, color });

  g.moveTo(14 * scale, 14 * scale);
  g.lineTo(18 * scale, 14 * scale);
  g.stroke({ width: 1.5 * scale, color });

  // Head
  g.circle(12 * scale, 4 * scale, 3 * scale);
  g.fill(color);
}

function drawCar(g: Graphics, scale: number, color: number): void {
  // Body
  g.roundRect(2 * scale, 10 * scale, 20 * scale, 8 * scale, 2 * scale);
  g.fill(color);

  // Roof
  g.moveTo(6 * scale, 10 * scale);
  g.lineTo(8 * scale, 5 * scale);
  g.lineTo(16 * scale, 5 * scale);
  g.lineTo(18 * scale, 10 * scale);
  g.fill(color);

  // Windows
  g.roundRect(8.5 * scale, 6 * scale, 3 * scale, 3.5 * scale, 0.5 * scale);
  g.fill(theme.colors.primaryBg);

  g.roundRect(12.5 * scale, 6 * scale, 3 * scale, 3.5 * scale, 0.5 * scale);
  g.fill(theme.colors.primaryBg);

  // Wheels
  g.circle(6 * scale, 18 * scale, 2.5 * scale);
  g.fill(theme.colors.neutralDark);

  g.circle(18 * scale, 18 * scale, 2.5 * scale);
  g.fill(theme.colors.neutralDark);
}

function drawTrafficLight(g: Graphics, scale: number, color: number): void {
  // Housing
  g.roundRect(8 * scale, 2 * scale, 8 * scale, 18 * scale, 2 * scale);
  g.stroke({ width: 1.5 * scale, color });

  // Lights
  g.circle(12 * scale, 6 * scale, 2 * scale);
  g.fill(theme.colors.risk);

  g.circle(12 * scale, 11 * scale, 2 * scale);
  g.fill(theme.colors.decision);

  g.circle(12 * scale, 16 * scale, 2 * scale);
  g.fill(theme.colors.safe);

  // Pole
  g.rect(11 * scale, 20 * scale, 2 * scale, 4 * scale);
  g.fill(color);
}

function drawCrosswalk(g: Graphics, scale: number, color: number): void {
  // Stripes
  for (let i = 0; i < 5; i++) {
    g.rect(4 * scale, (4 + i * 4) * scale, 16 * scale, 2 * scale);
    g.fill(color);
  }
}

function drawWarning(g: Graphics, scale: number, color: number): void {
  // Triangle
  g.moveTo(12 * scale, 3 * scale);
  g.lineTo(22 * scale, 21 * scale);
  g.lineTo(2 * scale, 21 * scale);
  g.closePath();
  g.stroke({ width: 2 * scale, color });

  // Exclamation
  g.rect(11 * scale, 9 * scale, 2 * scale, 6 * scale);
  g.fill(color);

  g.circle(12 * scale, 18 * scale, 1.5 * scale);
  g.fill(color);
}

function drawCheck(g: Graphics, scale: number, color: number): void {
  g.moveTo(4 * scale, 12 * scale);
  g.lineTo(10 * scale, 18 * scale);
  g.lineTo(20 * scale, 6 * scale);
  g.stroke({ width: 2.5 * scale, color });
}

function drawEye(g: Graphics, scale: number, color: number): void {
  // Eye shape
  g.moveTo(2 * scale, 12 * scale);
  g.quadraticCurveTo(12 * scale, 4 * scale, 22 * scale, 12 * scale);
  g.quadraticCurveTo(12 * scale, 20 * scale, 2 * scale, 12 * scale);
  g.stroke({ width: 1.5 * scale, color });

  // Iris
  g.circle(12 * scale, 12 * scale, 4 * scale);
  g.fill(color);

  // Pupil
  g.circle(12 * scale, 12 * scale, 2 * scale);
  g.fill(theme.colors.primaryBg);
}

function drawClock(g: Graphics, scale: number, color: number): void {
  // Face
  g.circle(12 * scale, 12 * scale, 9 * scale);
  g.stroke({ width: 1.5 * scale, color });

  // Hour hand
  g.moveTo(12 * scale, 12 * scale);
  g.lineTo(12 * scale, 7 * scale);
  g.stroke({ width: 2 * scale, color });

  // Minute hand
  g.moveTo(12 * scale, 12 * scale);
  g.lineTo(17 * scale, 12 * scale);
  g.stroke({ width: 1.5 * scale, color });

  // Center
  g.circle(12 * scale, 12 * scale, 1.5 * scale);
  g.fill(color);
}

function drawArrowRight(g: Graphics, scale: number, color: number): void {
  g.moveTo(4 * scale, 12 * scale);
  g.lineTo(18 * scale, 12 * scale);
  g.stroke({ width: 2 * scale, color });

  g.moveTo(14 * scale, 7 * scale);
  g.lineTo(20 * scale, 12 * scale);
  g.lineTo(14 * scale, 17 * scale);
  g.stroke({ width: 2 * scale, color });
}

/**
 * Create a user position marker with icon
 */
export function createUserMarker(role: 'pedestrian' | 'cyclist' | 'driver', size = 48): Container {
  const container = new Container();

  // Background circle
  const bg = new Graphics();
  bg.circle(0, 0, size / 2);
  bg.fill(theme.colors.decision);
  bg.circle(0, 0, size / 2);
  bg.stroke({ width: 3, color: theme.colors.textPrimary });
  container.addChild(bg);

  // Icon
  const icon = createIcon(role, size * 0.6, theme.colors.textDark);
  icon.position.set(-size * 0.3, -size * 0.3);
  container.addChild(icon);

  return container;
}

/**
 * Create a labeled icon (icon with text below)
 */
export function createLabeledIcon(
  name: IconName,
  label: string,
  iconSize = 32,
  color: number = theme.colors.textPrimary
): Container {
  const container = new Container();

  const icon = createIcon(name, iconSize, color);
  icon.position.set(-iconSize / 2, 0);
  container.addChild(icon);

  const text = new Text({
    text: label,
    style: new TextStyle({
      fontFamily: theme.typography.fontFamily,
      fontSize: theme.typography.sizes.xs,
      fontWeight: theme.typography.weights.semibold,
      fill: color,
    }),
  });
  text.anchor.set(0.5, 0);
  text.position.set(0, iconSize + 4);
  container.addChild(text);

  return container;
}
